import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  FiPlus, FiTrash2, FiEdit2, FiX, FiSave, FiRefreshCw,
  FiUser, FiVideo, FiCalendar, FiBook, FiUsers
} from 'react-icons/fi';
import {
  getAdminLiveClasses, createLiveClass, updateLiveClass, deleteLiveClass,
  addReplayLink, getAdminTeachers, createTeacher, updateTeacher, deleteTeacher,
  getAdminCourses
} from '../utils/api';

/* ─── Helpers ─── */
const statusStyle = {
  scheduled: { bg: '#f0f4ff', color: '#2563eb', label: '📅 Scheduled' },
  live:      { bg: '#f0fdf4', color: '#16a34a', label: '🔴 Live' },
  ended:     { bg: '#f8fafc', color: '#64748b', label: '✅ Ended' },
  cancelled: { bg: '#fef2f2', color: '#dc2626', label: '❌ Cancelled' },
};

const Spin = () => <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>;

/* ─── Teacher Manager ─── */
const TeacherManager = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form,     setForm]     = useState({ fullName: '', email: '', password: '', phone: '', teacherSubjects: '', teacherBio: '' });
  const [saving,   setSaving]   = useState(false);

  const load = () => {
    setLoading(true);
    getAdminTeachers().then(r => setTeachers(r.data.teachers || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd  = () => { setForm({ fullName: '', email: '', password: '', phone: '', teacherSubjects: '', teacherBio: '' }); setEditItem(null); setModal(true); };
  const openEdit = (t) => {
    setForm({ fullName: t.fullName, email: t.email, password: '', phone: t.phone || '', teacherSubjects: (t.teacherSubjects || []).join(', '), teacherBio: t.teacherBio || '' });
    setEditItem(t); setModal(true);
  };

  const save = async () => {
    if (!form.fullName || !form.email) { toast.error('Name and email required'); return; }
    if (!editItem && !form.password) { toast.error('Password required for new teacher'); return; }
    setSaving(true);
    try {
      const data = { ...form, teacherSubjects: form.teacherSubjects.split(',').map(s => s.trim()).filter(Boolean) };
      if (editItem) { await updateTeacher(editItem._id, data); toast.success('Teacher updated ✅'); }
      else          { await createTeacher(data);               toast.success('Teacher account created ✅'); }
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete ${t.fullName}? They will lose access.`)) return;
    try { await deleteTeacher(t._id); toast.success('Teacher deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 500, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h3 style={{ margin: 0 }}>{editItem ? 'Edit Teacher' : 'Add Teacher'}</h3>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}><FiX /></button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name *</label>
                <input value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} className="form-control" placeholder="Rahul Sharma" />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="form-control" placeholder="teacher@email.com" disabled={!!editItem} />
              </div>
              <div className="form-group">
                <label>{editItem ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="form-control" placeholder="Min 6 characters" />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="form-control" placeholder="9876543210" />
              </div>
            </div>
            <div className="form-group">
              <label>Subjects <span style={{ fontSize: 12, color: 'var(--mid)', fontWeight: 400 }}>(comma separated)</span></label>
              <input value={form.teacherSubjects} onChange={e => setForm(f => ({ ...f, teacherSubjects: e.target.value }))} className="form-control" placeholder="React, Node.js, Python" />
            </div>
            <div className="form-group">
              <label>Bio</label>
              <textarea value={form.teacherBio} onChange={e => setForm(f => ({ ...f, teacherBio: e.target.value }))} className="form-control" rows={2} placeholder="5 years experience in Full Stack Development..." />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(false)} className="btn btn-outline">Cancel</button>
              <button onClick={save} disabled={saving} className="btn btn-primary">
                {saving ? 'Saving...' : editItem ? 'Update Teacher' : 'Create Teacher'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ margin: 0 }}>👨‍🏫 Teachers ({teachers.length})</h4>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} className="btn btn-outline btn-sm"><FiRefreshCw size={12} /></button>
          <button onClick={openAdd} className="btn btn-primary btn-sm"><FiPlus size={13} /> Add Teacher</button>
        </div>
      </div>

      {loading ? <Spin /> : teachers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--mid)', background: 'white', border: '1px dashed var(--border)', borderRadius: 12 }}>
          No teachers yet. Add a teacher to get started.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {teachers.map(t => (
            <div key={t._id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--accent))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>
                {t.fullName[0]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{t.fullName}</div>
                <div style={{ fontSize: 12, color: 'var(--mid)' }}>{t.email}{t.phone ? ' · ' + t.phone : ''}</div>
                {t.teacherSubjects?.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    {t.teacherSubjects.map((s, i) => (
                      <span key={i} style={{ background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>{s}</span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => openEdit(t)} className="btn btn-outline btn-sm"><FiEdit2 size={12} /> Edit</button>
                <button onClick={() => remove(t)} className="btn btn-sm" style={{ background: '#fee2e2', color: 'var(--error)' }}><FiTrash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Class Scheduler ─── */
const ClassScheduler = () => {
  const [classes,   setClasses]   = useState([]);
  const [teachers,  setTeachers]  = useState([]);
  const [courses,   setCourses]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [replayModal, setReplayModal] = useState(null);
  const [replayUrl,   setReplayUrl]   = useState('');
  const [form, setForm] = useState({
    title: '', subject: '', description: '', teacherId: '', courseId: '',
    scheduledAt: '', duration: 60, adminNote: '', forCourse: true,
  });

  const load = () => {
    setLoading(true);
    Promise.all([getAdminLiveClasses(), getAdminTeachers(), getAdminCourses()])
      .then(([c, t, co]) => {
        setClasses(c.data.classes || []);
        setTeachers(t.data.teachers || []);
        setCourses(co.data.courses || []);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd = () => {
    setForm({ title: '', subject: '', description: '', teacherId: '', courseId: '', scheduledAt: '', duration: 60, adminNote: '', forCourse: true });
    setEditItem(null); setModal(true);
  };
  const openEdit = (c) => {
    setForm({
      title:       c.title       || '',
      subject:     c.subject     || '',
      description: c.description || '',
      teacherId:   c.teacher?._id || c.teacher || '',
      courseId:    c.course?._id  || c.course  || '',
      scheduledAt: c.scheduledAt ? new Date(c.scheduledAt).toISOString().slice(0, 16) : '',
      duration:    c.duration    || 60,
      adminNote:   c.adminNote   || '',
      forCourse:   c.forCourse   ?? true,
    });
    setEditItem(c); setModal(true);
  };

  const save = async () => {
    if (!form.title || !form.subject || !form.teacherId || !form.scheduledAt) {
      toast.error('Title, subject, teacher and schedule time are required'); return;
    }
    setSaving(true);
    try {
      if (editItem) { await updateLiveClass(editItem._id, form); toast.success('Class updated ✅'); }
      else          { await createLiveClass(form);               toast.success('Class scheduled ✅'); }
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const remove = async (cls) => {
    if (!window.confirm(`Delete "${cls.title}"?`)) return;
    try { await deleteLiveClass(cls._id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const saveReplay = async () => {
    if (!replayUrl.trim()) { toast.error('Enter replay URL'); return; }
    try {
      await addReplayLink(replayModal, { replayUrl, availableAfterMinutes: 60 });
      toast.success('Replay link saved! Available to students in 1 hour ✅');
      setReplayModal(null); setReplayUrl(''); load();
    } catch { toast.error('Failed'); }
  };

  const updateStatus = async (cls, status) => {
    try { await updateLiveClass(cls._id, { status }); toast.success('Status updated'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div>
      {/* Schedule modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 580, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h3 style={{ margin: 0 }}>{editItem ? 'Edit Class' : 'Schedule Live Class'}</h3>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}><FiX /></button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Class Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="form-control" placeholder="Week 1 - HTML Basics" />
              </div>
              <div className="form-group">
                <label>Subject *</label>
                <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="form-control" placeholder="HTML & CSS" />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="form-control" rows={2} placeholder="What will be covered in this class..." />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Assign Teacher *</label>
                <select value={form.teacherId} onChange={e => setForm(f => ({ ...f, teacherId: e.target.value }))} className="form-control">
                  <option value="">— Select Teacher —</option>
                  {teachers.map(t => <option key={t._id} value={t._id}>{t.fullName}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Assign to Course</label>
                <select value={form.courseId} onChange={e => setForm(f => ({ ...f, courseId: e.target.value }))} className="form-control">
                  <option value="">— Select Course (optional) —</option>
                  {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Date & Time *</label>
                <input type="datetime-local" value={form.scheduledAt} onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))} className="form-control" />
              </div>
              <div className="form-group">
                <label>Duration (minutes)</label>
                <input type="number" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: Number(e.target.value) }))} className="form-control" min={15} max={240} />
              </div>
            </div>
            <div className="form-group">
              <label>Visible to</label>
              <select value={form.forCourse ? 'course' : 'specific'} onChange={e => setForm(f => ({ ...f, forCourse: e.target.value === 'course' }))} className="form-control">
                <option value="course">All students enrolled in selected course</option>
                <option value="specific">Specific students only (add via student IDs)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Note to Teacher <span style={{ fontSize: 12, color: 'var(--mid)', fontWeight: 400 }}>(shown on their dashboard)</span></label>
              <textarea value={form.adminNote} onChange={e => setForm(f => ({ ...f, adminNote: e.target.value }))} className="form-control" rows={2} placeholder="Please cover these topics: ..." />
            </div>
            <div className="form-group">
                  <label>Google Meet Link <span style={{ fontSize: 12, color: 'var(--mid)', fontWeight: 400 }}>(students and teacher will join this)</span></label>
                     <input
                      type="text"
                          className="form-control"
                              placeholder="https://meet.google.com/abc-defg-hij"
                         value={form.meetLink || ''}
                                onChange={e => setForm(f => ({ ...f, meetLink: e.target.value }))}
                              />
                         </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(false)} className="btn btn-outline">Cancel</button>
              <button onClick={save} disabled={saving} className="btn btn-primary">
                {saving ? 'Saving...' : editItem ? 'Update Class' : 'Schedule Class'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replay modal */}
      {replayModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%' }}>
            <h3 style={{ marginBottom: 16 }}>📹 Add Replay Link</h3>
            <p style={{ color: 'var(--mid)', fontSize: 14, marginBottom: 20 }}>
              Upload recording to YouTube (unlisted) or Google Drive. Students can access it after 1 hour.
            </p>
            <div className="form-group">
              <label>Recording URL</label>
              <input value={replayUrl} onChange={e => setReplayUrl(e.target.value)} className="form-control" placeholder="https://youtube.com/watch?v=... or Google Drive link" autoFocus />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setReplayModal(null); setReplayUrl(''); }} className="btn btn-outline">Cancel</button>
              <button onClick={saveReplay} className="btn btn-primary">Save Replay Link</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ margin: 0 }}>📹 Live Classes ({classes.length})</h4>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} className="btn btn-outline btn-sm"><FiRefreshCw size={12} /></button>
          <button onClick={openAdd} className="btn btn-primary btn-sm"><FiPlus size={13} /> Schedule Class</button>
        </div>
      </div>

      {loading ? <Spin /> : classes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--mid)', background: 'white', border: '1px dashed var(--border)', borderRadius: 12 }}>
          No classes scheduled yet. Click "Schedule Class" to create one.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {classes.map(cls => {
            const st = statusStyle[cls.status] || statusStyle.scheduled;
            return (
              <div key={cls._id} style={{ background: 'white', border: `1px solid ${cls.status === 'live' ? '#16a34a' : 'var(--border)'}`, borderRadius: 12, padding: '16px 20px', borderWidth: cls.status === 'live' ? 2 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{cls.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--mid)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <span><FiBook size={12} style={{ marginRight: 4 }} />{cls.subject}</span>
                      <span><FiUser size={12} style={{ marginRight: 4 }} />{cls.teacher?.fullName || '—'}</span>
                      {cls.course && <span><FiUsers size={12} style={{ marginRight: 4 }} />{cls.course.title}</span>}
                      <span><FiCalendar size={12} style={{ marginRight: 4 }} />
                      {new Date(cls.scheduledAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })} at {new Date(cls.scheduledAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                      </span>
                      <span>{cls.duration} min</span>
                    </div>
                  </div>
                  <span style={{ background: st.bg, color: st.color, padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                    {st.label}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                  <button onClick={() => openEdit(cls)} className="btn btn-outline btn-sm"><FiEdit2 size={12} /> Edit</button>
                  {cls.status === 'scheduled' && <button onClick={() => updateStatus(cls, 'live')} className="btn btn-sm" style={{ background: '#f0fdf4', color: '#16a34a', fontWeight: 700 }}>🔴 Go Live</button>}
                  {cls.status === 'live' && <button onClick={() => updateStatus(cls, 'ended')} className="btn btn-sm" style={{ background: '#fef2f2', color: '#dc2626' }}>⏹ End Class</button>}
                  {cls.status === 'ended' && !cls.replayUrl && <button onClick={() => { setReplayModal(cls._id); setReplayUrl(''); }} className="btn btn-outline btn-sm"><FiVideo size={12} /> Add Replay</button>}
                  {cls.replayUrl && <span style={{ fontSize: 12, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 4 }}>✅ Replay added</span>}
                  <button onClick={() => remove(cls)} className="btn btn-sm" style={{ background: '#fee2e2', color: 'var(--error)' }}><FiTrash2 size={12} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── Main Export ─── */
export default function AdminLiveClasses() {
  const [tab, setTab] = useState('classes');

  return (
    <div style={{ padding: 24 }}>
      <style>{`
        .alc-tabs { display: flex; gap: 4; background: white; border: 1px solid var(--border); border-radius: 10px; padding: 5px; width: fit-content; margin-bottom: 24px; }
        .alc-tab { padding: 8px 20px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; font-family: var(--font-display); display: flex; align-items: center; gap: 7px; transition: 0.2s; }
        .alc-tab.on { background: var(--primary); color: white; }
        .alc-tab:not(.on) { background: none; color: var(--mid); }
        .alc-tab:not(.on):hover { background: var(--bg); color: var(--dark); }
      `}</style>

      <div className="alc-tabs">
        <button className={`alc-tab${tab === 'classes' ? ' on' : ''}`} onClick={() => setTab('classes')}>
          <FiVideo size={14} /> Live Classes
        </button>
        <button className={`alc-tab${tab === 'teachers' ? ' on' : ''}`} onClick={() => setTab('teachers')}>
          <FiUser size={14} /> Teachers
        </button>
      </div>

      {tab === 'classes'  && <ClassScheduler />}
      {tab === 'teachers' && <TeacherManager />}
    </div>
  );
}